import React, { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AgendaMotionDoc, AgendaMotionRow as Motion, MemberIndexEntry } from '../../types/types';
import { buttonReset, color, font, radius, space, visuallyHidden, weight } from '../../theme';
import { photoFor } from '../../utils/names';
import { Badge } from '../Badge';
import Avatar from '../Avatar';
import { ButtonLink } from '../Button';
import Icon from '../Icon';
import { discussionLabel, initiatorCount, motionDate, motionStatusTone, motionSubject, motionTypeTone, motionUrl } from './agendaMotions';

interface AgendaMotionRowProps {
  motion: Motion;
  // The MKs of the motion's Knesset (names and photos of the initiators)
  members: Record<string, MemberIndexEntry>;
  // Committee doc ID → name; the committee is left out of the meta line when `hideCommittee`
  committees: Record<string, string>;
  hideCommittee?: boolean;
  // Line above the row; off for the first row in a card
  divider?: boolean;
  // The back label for the MK pages it links to
  from: string;
}

const panelLabel: React.CSSProperties = { fontSize: font.caption, fontWeight: weight.bold, color: color.textSecondary };

// An initiator: a link to their page when they have a member doc, else the name as text
const InitiatorChip = ({ id, name, member, from }: { id?: string; name: string; member?: MemberIndexEntry; from: string }) => {
  const pill: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: space.xs + 2, minHeight: 32, boxSizing: 'border-box',
    padding: `2px ${space.md - 2}px 2px 3px`, borderRadius: radius.pill, background: color.surface,
    fontSize: font.small, color: color.text, textDecoration: 'none',
  };
  const inner = (
    <>
      <Avatar name={name} src={member ? photoFor(member, 26) : undefined} size={26} />
      {name}
    </>
  );
  // The visible pill is 32px; the link's padding makes the tap target 40px
  return id
    ? <Link to={`/members/${id}`} state={{ from, stat: 'agendaMotions' }} style={{ display: 'inline-flex', padding: '4px 0', textDecoration: 'none' }}><span style={{ ...pill, color: color.primary, fontWeight: weight.medium }}>{inner}</span></Link>
    : <span style={{ display: 'inline-flex', padding: '4px 0' }}><span style={pill}>{inner}</span></span>;
};

// A proposal document's link (.pdf opens, .docx downloads); `who` names its MK for screen readers
const ProposalLink = ({ doc, who }: { doc: AgendaMotionDoc; who?: string }) => (
  <ButtonLink href={doc.url} icon="document">
    ההצעה{who && <span style={visuallyHidden}> של {who}</span>}
  </ButtonLink>
);

/* One motion for the agenda in a list card (5a): its subject, type and status badges and a
   meta line (committee · date · initiators). Pressing it opens it in place: the MKs who
   moved it (linked), the plenum and committee discussions, and its documents (`docs`):
   - one proposal: the "ההצעה" button opens it;
   - a combined motion's proposals, one per MK: an "ההצעה" link beside each initiator, and
     any document no initiator matches after them, with its name;
   - the committee's conclusions: a "מסקנות הוועדה" link;
   - no proposal document (not synced yet, or none): the motion's page on the Knesset site.
   The date is PresidentDecisionDate, else the last update (most rows), so it's labelled
   "עודכן". */
const AgendaMotionRow = ({ motion: m, members, committees, hideCommittee, divider = true, from }: AgendaMotionRowProps) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const count = initiatorCount(m);
  const firstName = m.mkIds.length > 0 ? (members[m.mkIds[0]] ? members[m.mkIds[0]].name : '') : (m.otherNames || [])[0] || '';
  const initiators = count === 1 ? firstName : count > 1 ? `${count} יוזמים` : '';
  const date = motionDate(m.date);
  const committee = !hideCommittee && m.committeeId ? committees[m.committeeId] : '';
  const meta = [committee, date ? `עודכן ${date}` : '', initiators].filter(Boolean);
  const url = motionUrl(m.id);
  const discussions = m.discussions || [];
  // MKs with a member doc first (linked), then the names no doc matched
  const known = m.mkIds.filter(id => !!members[id]);
  const others = m.otherNames || [];
  const docs = m.docs || [];
  const proposals = docs.filter(d => d.kind === 'proposal' && !!d.url);
  const conclusions = docs.find(d => d.kind === 'conclusions' && !!d.url);
  // Several proposals (a combined motion): each initiator's own, by member doc or by name
  const perMk = proposals.length > 1;
  const used: Record<string, boolean> = {};
  const docOf = (id: string | null, name: string): AgendaMotionDoc | undefined => {
    if (!perMk) return undefined;
    const d = (id ? proposals.find(x => x.mkId === id) : undefined) || proposals.find(x => !x.mkId && x.name === name);
    if (d) used[d.url] = true;
    return d;
  };
  const knownDocs = known.map(id => docOf(id, members[id].name));
  const otherDocs = others.map(n => docOf(null, n));
  const unmatched = perMk ? proposals.filter(d => !used[d.url]) : [];
  const docName = (d: AgendaMotionDoc) => (d.mkId && members[d.mkId] ? members[d.mkId].name : d.name) || 'ח״כ';
  // An initiator, with their proposal's link beside them when there is one
  const withDoc = (key: string, chip: React.ReactNode, doc: AgendaMotionDoc | undefined, who: string) => (doc
    ? <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs }}>{chip}<ProposalLink doc={doc} who={who} /></span>
    : <React.Fragment key={key}>{chip}</React.Fragment>);

  return (
    <div style={{ borderTop: divider ? `1px solid ${color.divider}` : 'none' }}>
      <button
        type="button"
        data-motion-toggle=""
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-start', gap: space.md, minHeight: 56, padding: `${space.sm + 2}px ${space.lg}px` }}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xxs }}>
          <span style={{
            fontSize: font.body, fontWeight: weight.medium, lineHeight: 1.35, color: color.text,
            ...(open ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
          }}>
            {motionSubject(m.subject)}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xs, fontSize: font.small, color: color.textSecondary, lineHeight: 1.35 }}>
            <span style={{ display: 'flex', gap: space.xs + 2, flexWrap: 'wrap' }}>
              {m.type && <Badge tone={motionTypeTone(m.type)}>{m.type}</Badge>}
              {m.status && <Badge tone={motionStatusTone(m.status)}>{m.status}</Badge>}
            </span>
            {meta.length > 0 && <span>{meta.join(' · ')}</span>}
          </span>
        </span>
        <span style={{ color: color.textTertiary, paddingTop: 2 }}><Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} /></span>
      </button>

      {open && (
        <div id={panelId} style={{
          // Under the text column: clear of the chevron at the end
          marginTop: -space.xs, marginBottom: space.md, marginInlineStart: space.lg, marginInlineEnd: space.lg + 18 + space.md,
          background: color.surfaceMuted, borderRadius: radius.md + 2, padding: `${space.sm + 2}px ${space.md}px`,
          display: 'flex', flexDirection: 'column', gap: space.sm,
        }}>
          {count > 0 && (
            <div>
              <div style={panelLabel}>{m.combined ? 'יוזמים (הצעה כוללת)' : count === 1 ? 'יוזם/ת' : 'יוזמים'}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: perMk ? space.md : space.xs + 2 }}>
                {known.map((id, i) => withDoc(id, <InitiatorChip id={id} name={members[id].name} member={members[id]} from={from} />, knownDocs[i], members[id].name))}
                {others.map((n, i) => withDoc(n, <InitiatorChip name={n} from={from} />, otherDocs[i], n))}
              </div>
              {unmatched.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2, marginTop: space.xs }}>
                  {unmatched.map(d => <ButtonLink key={d.url} href={d.url} icon="document">ההצעה של {docName(d)}</ButtonLink>)}
                </div>
              )}
            </div>
          )}
          <div>
            <div style={{ ...panelLabel, marginBottom: space.xxs }}>דיונים</div>
            {discussions.length === 0
              ? <div style={{ fontSize: font.caption, color: color.textTertiary }}>טרם התקיים דיון</div>
              : discussions.map(d => (
                <a key={d.url} href={d.url} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, minHeight: 36, fontSize: font.small, color: color.primary, textDecoration: 'none' }}>
                  <Icon name="external" size={15} />
                  <span style={{ fontWeight: weight.medium }}>{discussionLabel(d.label)}</span>
                  {d.date && <span style={{ color: color.textSecondary }}>{motionDate(d.date)}</span>}
                </a>
              ))}
          </div>
          {(proposals.length === 1 || conclusions || (proposals.length === 0 && url)) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
              {proposals.length === 1 && <ProposalLink doc={proposals[0]} />}
              {proposals.length === 0 && url && <ButtonLink href={url} icon="external" variant="neutral">ההצעה באתר הכנסת</ButtonLink>}
              {conclusions && <ButtonLink href={conclusions.url} icon="document">מסקנות הוועדה</ButtonLink>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgendaMotionRow;
